import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { teamService } from '@/services/team-service';
import type { TeamMember } from '@/types/core/team';
import type { Page } from '@/types/shared/page';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { teamMutationKeys, teamQueryKeys } from './query-keys';

/**
 * Hook to add multiple members to a team.
 * Returns a react-query mutation that accepts `{ userIds: UUID[] }`.
 * Applies optimistic updates and polls for each member to be available.
 */
export function useAddTeamMembers(tenantId?: UUID, teamId?: UUID) {
  const qc = useQueryClient();

  type Context = {
    previousData?: Array<
      [
        queryKey: readonly unknown[],
        data: InfiniteData<Page<TeamMember>> | undefined,
      ]
    >;
  };

  return useMutation<TeamMember[], Error, { userIds: UUID[] }, Context>({
    mutationKey: teamMutationKeys.addTeamMembers(tenantId, teamId),
    mutationFn: async (payload: { userIds: UUID[] }) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!teamId) throw new Error('Missing team id');

      // Add all members in parallel
      await Promise.all(
        payload.userIds.map((userId) =>
          teamService.assignTeamMember(tenantId, teamId, userId)
        )
      );

      // Poll for each member to be available
      const members = await Promise.all(
        payload.userIds.map((userId) =>
          pollForResource(
            () => teamService.findTeamMember(tenantId, teamId, userId),
            { maxAttempts: 10, delayMs: 300 }
          )
        )
      );

      return members.filter((member) => member !== null);
    },
    onMutate: async (payload: { userIds: UUID[] }) => {
      if (!tenantId || !teamId) return { previousData: [] };

      // Cancel any outgoing refetches
      await qc.cancelQueries({
        queryKey: teamQueryKeys.memberList(tenantId, teamId),
      });

      // Snapshot previous data
      const previousData = qc.getQueriesData<InfiniteData<Page<TeamMember>>>({
        queryKey: teamQueryKeys.memberList(tenantId, teamId),
      });

      // Create optimistic team members
      const optimisticMembers: TeamMember[] = payload.userIds.map((userId) => ({
        userId,
        name: 'Loading...',
        email: undefined,
        assigned: true,
      }));

      // Optimistically update cache
      qc.setQueriesData<InfiniteData<Page<TeamMember>>>(
        { queryKey: teamQueryKeys.memberList(tenantId, teamId) },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page, index) =>
              index === 0
                ? {
                    ...page,
                    items: [...optimisticMembers, ...page.items],
                    total: (page.total ?? 0) + optimisticMembers.length,
                  }
                : page
            ),
          };
        }
      );

      return { previousData };
    },
    onError: (_error, _payload, context) => {
      // Rollback optimistic update on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (members) => {
      if (!tenantId || !teamId || !members.length) return;

      // Update cache with polled members
      qc.setQueriesData<InfiniteData<Page<TeamMember>>>(
        { queryKey: teamQueryKeys.memberList(tenantId, teamId) },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page, index) =>
              index === 0
                ? {
                    ...page,
                    items: [
                      ...members,
                      ...page.items.filter(
                        (item) => !members.some((m) => m.userId === item.userId)
                      ),
                    ],
                  }
                : page
            ),
          };
        }
      );

      // Also invalidate teams query in case member counts are shown
      qc.invalidateQueries({
        queryKey: teamQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
    // onSuccess reconciles only the members the read model had already
    // projected, and keeps every other row — which is the `name: 'Loading...'`
    // placeholder onMutate inserted for a member whose projection lagged. It
    // also early-returns when nothing projected at all. Nothing above
    // invalidates the member list, so that placeholder used to persist until
    // the user navigated away.
    //
    // It closes a second one too: onMutate raises `page.total` by the number of
    // placeholders and onSuccess never puts it back, so the count was wrong by
    // however many members had not projected.
    //
    // This refetch makes onSuccess's setQueriesData block above a flicker guard
    // rather than the reconciliation — `refetchType: 'all'` overwrites whatever
    // it painted, within one refetch. Worth keeping for the smoother paint, but
    // it is no longer what makes the list correct.
    //
    // On both outcomes deliberately: on success it replaces any placeholder
    // with the real row, and on error it confirms the rollback against the
    // server.
    onSettled: () => {
      if (!tenantId || !teamId) return;

      qc.invalidateQueries({
        queryKey: teamQueryKeys.memberList(tenantId, teamId),
        refetchType: 'all',
      });
    },
  });
}
